import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { RECOVERY_USER_EMAIL } from "../../constants";
import SectionCard from "./components/SectionCard";
import CreateUserForm from "./components/users/CreateUserForm";
import ResetPasswordDialog from "./components/users/ResetPasswordDialog.jsx";
import UsersTable from "./components/users/UsersTable";
import SessionsTable from "./components/users/SessionsTable";
import CampusEditor from "./components/campuses/CampusEditor.jsx";
import SupervisionEditor from "./components/supervision/SupervisionEditor.jsx";
import AuditFilters from "./components/audit/AuditFilters";
import AuditTable from "./components/audit/AuditTable";
import HubspotMappingsTable from "./components/hubspot/HubspotMappingsTable";
import AppSettingsTable from "./components/settings/AppSettingsTable";

import useUsers from "./hooks/useUsers";
import useSessions from "./hooks/useSessions";
import useCampuses from "./hooks/useCampuses";
import useAudit from "./hooks/useAudit";
import useStudentSearch from "./hooks/useStudentSearch";
import useHubspotMappings from "./hooks/useHubspotMappings";

import adminApi from "./api/adminApi";
import { ADMIN_TABS } from "./constants/adminConstants";
import { errorText } from "./utils/adminUtils";

export default function AdminPage({ selectedYear }) {
  const [tab, setTab] = useState(ADMIN_TABS.USERS);
  const [banner, setBanner] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);

  const { users: allUsers, usersLoading, loadUsers } = useUsers(setBanner);
  // Hide the break-glass recovery account from the admin user list.
  const users = (allUsers || []).filter(
    (u) => String(u?.email || "").toLowerCase() !== RECOVERY_USER_EMAIL
  );
  const { sessions, sessionsLoading, sessionSummary, loadSessions } = useSessions(setBanner);
  const { campuses, campusesLoading, loadCampuses } = useCampuses(setBanner);
  const { mappings, mappingsLoading, loadMappings } = useHubspotMappings(setBanner);

  const {
    auditFilters,
    setAuditFilters,
    registerAuditLoading,
    auditLoading,
    registerAuditRows,
    auditRows,
    loadRegisterAudit,
    loadAudit,
    applyAuditFilters,
    clearAuditFilters,
  } = useAudit(setBanner);

  const {
    studentOptions,
    studentSearchLoading,
    setStudentSearchTerm,
    clearStudentSearch,
  } = useStudentSearch(setBanner, selectedYear);

  const loadEverything = useCallback(() => {
    loadUsers();
    loadSessions();
    loadCampuses();
  }, [loadUsers, loadSessions, loadCampuses]);

  useEffect(() => {
    loadEverything();
  }, [loadEverything]);

  // Lazy — the mapping endpoint only exists once the HubSpot import backend is
  // deployed, so don't nag every admin visit with a 404 banner until it is.
  useEffect(() => {
    if (tab === ADMIN_TABS.HUBSPOT) loadMappings();
  }, [tab, loadMappings]);

  const handleLogoutUser = async (session) => {
    const confirmed = window.confirm(`Log out ${session.email} from all active sessions?`);
    if (!confirmed) return;

    try {
      await adminApi.logoutUser(session.userId);
      await loadSessions();
      setBanner({ type: "success", text: `Logged out ${session.email}` });
    } catch (error) {
      setBanner({ type: "error", text: error.message || "Failed to log out user" });
    }
  };

  const handleAuditRefreshAfterUserAction = useCallback(() => {
    if (
      auditFilters.email ||
      auditFilters.studentId ||
      auditFilters.startDate ||
      auditFilters.endDate
    ) {
      loadRegisterAudit(false, auditFilters);
      loadAudit(false, auditFilters);
    }
  }, [auditFilters, loadRegisterAudit, loadAudit]);

  const handleClearAuditFilters = useCallback(() => {
    clearAuditFilters();
    clearStudentSearch();
  }, [clearAuditFilters, clearStudentSearch]);

  // Changing the level signs the holder out everywhere (server-side), so it
  // is confirmed. The backend refuses an admin demoting themselves (403).
  const handleChangeRole = async (user, nextRole) => {
    if (!nextRole || nextRole === user.role) return;
    const confirmed = window.confirm(
      `Change ${user.email} from ${user.role} to ${nextRole}? They will be signed out.`
    );
    if (!confirmed) return;

    try {
      await adminApi.updateUserRole(user.id, nextRole);
      await loadUsers();
      setBanner({ type: "success", text: `${user.email} is now ${nextRole}` });
    } catch (error) {
      setBanner({ type: "error", text: errorText(error, "Failed to update role") });
    }
  };

  // Grants take effect on the holder's next request — no sign-out, no confirm.
  const handleToggleGrant = async (user, grant) => {
    const current = user.grants || [];
    const next = current.includes(grant)
      ? current.filter((g) => g !== grant)
      : [...current, grant];

    try {
      await adminApi.updateUserGrants(user.id, next);
      await loadUsers();
      setBanner({
        type: "success",
        text: `${user.email}: ${next.length ? next.join(", ") : "no grants"}`,
      });
    } catch (error) {
      setBanner({ type: "error", text: errorText(error, "Failed to update grants") });
    }
  };

  const handleToggleUserEnabled = async (user) => {
    const nextEnabled = !user.enabled;
    const actionLabel = nextEnabled ? "enable" : "disable";

    const confirmed = window.confirm(
      `Are you sure you want to ${actionLabel} ${user.email}?`
    );
    if (!confirmed) return;

    try {
      await adminApi.updateUserEnabled(user.id, nextEnabled);
      await loadUsers();
      await loadSessions();

      setBanner({
        type: "success",
        text: `${nextEnabled ? "Enabled" : "Disabled"} ${user.email}`,
      });
    } catch (error) {
      setBanner({
        type: "error",
        text: error.message || `Failed to ${actionLabel} user`,
      });
    }
  };

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4">Admin</Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Chip label={`${users.length} users`} />
            <Chip label={sessionSummary} color="success" />
            <IconButton onClick={loadEverything}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>

        {banner && (
          <Alert severity={banner.type} onClose={() => setBanner(null)}>
            {banner.text}
          </Alert>
        )}

        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Users" />
          <Tab label="Supervision" />
          <Tab label="Campuses" />
          <Tab label="Audit" />
          <Tab label="HubSpot" />
          <Tab label="Settings" />
        </Tabs>

        {tab === ADMIN_TABS.USERS && (
          <Stack spacing={2}>
            <SectionCard title="Create user account">
              <CreateUserForm
                setBanner={setBanner}
                onCreated={() => {
                  loadUsers();
                  handleAuditRefreshAfterUserAction();
                }}
              />
            </SectionCard>

            <SectionCard
              title="Current sessions"
              action={<Button size="small" onClick={loadSessions}>Refresh</Button>}
            >
              <SessionsTable
                sessions={sessions}
                loading={sessionsLoading}
                onLogoutClick={handleLogoutUser}
              />
            </SectionCard>

            <SectionCard title="Existing users">
              <UsersTable
                users={users}
                loading={usersLoading}
                onResetClick={setPasswordUser}
                onToggleEnabled={handleToggleUserEnabled}
                onChangeRole={handleChangeRole}
                onToggleGrant={handleToggleGrant}
              />
            </SectionCard>
          </Stack>
        )}

        {tab === ADMIN_TABS.SUPERVISION && (
          <SectionCard
            title="Supervision — who reviews whose hours"
            action={<Button size="small" onClick={loadUsers}>Refresh</Button>}
          >
            <SupervisionEditor users={users} setBanner={setBanner} />
          </SectionCard>
        )}

        {tab === ADMIN_TABS.CAMPUSES && (
          <SectionCard
            title="Campus management"
            action={<Button size="small" onClick={loadCampuses}>Refresh</Button>}
          >
            <CampusEditor
              campuses={campuses}
              loading={campusesLoading}
              onRefresh={loadCampuses}
              setBanner={setBanner}
              selectedYear={selectedYear}
            />
          </SectionCard>
        )}

        {tab === ADMIN_TABS.HUBSPOT && (
          <SectionCard
            title="HubSpot import — SKU to product mapping"
            action={<Button size="small" onClick={loadMappings}>Refresh</Button>}
          >
            <HubspotMappingsTable
              mappings={mappings}
              loading={mappingsLoading}
              campuses={campuses}
              onRefresh={loadMappings}
              setBanner={setBanner}
            />
          </SectionCard>
        )}

        {tab === ADMIN_TABS.SETTINGS && (
          <SectionCard title="Settings">
            <AppSettingsTable setBanner={setBanner} />
          </SectionCard>
        )}

        {tab === ADMIN_TABS.AUDIT && (
          <Stack spacing={2}>
            <SectionCard title="Audit filters">
              <AuditFilters
                filters={auditFilters}
                setFilters={setAuditFilters}
                studentOptions={studentOptions}
                studentSearchLoading={studentSearchLoading}
                onStudentSearch={setStudentSearchTerm}
                onApply={applyAuditFilters}
                onClear={handleClearAuditFilters}
              />
            </SectionCard>

            <SectionCard
              title="Register audit"
              action={
                <Button size="small" onClick={() => loadRegisterAudit(false, auditFilters)}>
                  Refresh
                </Button>
              }
            >
              <AuditTable
                rows={registerAuditRows}
                loading={registerAuditLoading}
                columns={[
                  { key: "id", label: "ID" },
                  { key: "timestamp", label: "Timestamp", sortable: true },
                  { key: "email", label: "Username", sortable: true },
                  { key: "action", label: "Action" },
                ]}
              />
            </SectionCard>

            <SectionCard
              title="Application audit"
              action={
                <Button size="small" onClick={() => loadAudit(false, auditFilters)}>
                  Refresh
                </Button>
              }
            >
              <AuditTable
                rows={auditRows}
                loading={auditLoading}
                columns={[
                  { key: "id", label: "ID" },
                  { key: "timestamp", label: "Timestamp", sortable: true },
                  { key: "email", label: "Username", sortable: true },
                  { key: "role", label: "Role" },
                  { key: "student_id", label: "Student ID", sortable: true },
                  { key: "action", label: "Action" },
                ]}
              />
            </SectionCard>
          </Stack>
        )}
      </Stack>

      <ResetPasswordDialog
        open={Boolean(passwordUser)}
        onClose={() => setPasswordUser(null)}
        user={passwordUser}
        setBanner={setBanner}
        onSaved={() => {
          loadUsers();
          loadSessions();
          handleAuditRefreshAfterUserAction();
        }}
      />
    </Box>
  );
}